import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';

// SVG Icons remain unchanged
const CloseIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const API_URL = '/api';
const SCREEN_NAME = 'VendorMaster'; // Added screen name constant

const VendorViewModal = ({ open, onClose, vendorCode }) => {
  const [loadingFile, setLoadingFile] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [vendorData, setVendorData] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [branches, setBranches] = useState([]); // Add state for branches
  
  // Department screen-config state
  const [screenConfig, setScreenConfig] = useState(null);

  const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken'); // Or sessionStorage, depending on where you saved it
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};
  
  // Function to fetch branches
  const fetchBranches = async () => {
    try {
      const response = await fetch(`${API_URL}/branches`,{headers: getAuthHeaders() });
      const data = await response.json();
      setBranches(data);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };
  
  // Function to fetch vendor addresses
  const fetchVendorAddresses = async (vendorCode) => {
    try {
      setIsLoadingAddresses(true);
      console.log(`Fetching addresses for vendor: ${vendorCode}`);
      
      const response = await fetch(`${API_URL}/vendors/${vendorCode}/addresses`,{headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const addressesData = await response.json();
      console.log('Fetched addresses:', addressesData);
      setAddresses(addressesData);
      return addressesData;
    } catch (error) {
      console.error('Error fetching vendor addresses:', error);
      setAddresses([]);
      return [];
    } finally {
      setIsLoadingAddresses(false);
    }
  };
  
  // Function to fetch vendor contacts
  const fetchVendorContacts = async (vendorCode) => {
    try {
      setIsLoadingContacts(true);
      console.log(`Fetching contacts for vendor: ${vendorCode}`);
      
      const response = await fetch(`${API_URL}/vendors/${vendorCode}/contacts`,{headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const contactsData = await response.json();
      console.log('Fetched contacts:', contactsData);
      setContacts(contactsData);
      return contactsData;
    } catch (error) {
      console.error('Error fetching vendor contacts:', error);
      setContacts([]);
      return [];
    } finally {
      setIsLoadingContacts(false);
    }
  };
  
  // --- Department screen-config helpers + fetch ---
  const truthy = (v) => {
    if (v === undefined || v === null) return false;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v === 1;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      return s === '1' || s === 'true' || s === 'yes' || s === 'on';
    }
    return Boolean(v);
  };
  
  const shouldShowDepartmentOnHeader = (cfg) => {
    if (!cfg) return false;
    const showHeader = truthy(cfg.ShowOnHeader) || truthy(cfg.showOnHeader);
    const showScreen = truthy(cfg.ShowOnScreen) || truthy(cfg.showOnScreen);
    return showHeader && showScreen;
  };
  
  const fetchScreenConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/screen-config/${encodeURIComponent(SCREEN_NAME)}`, {
        method: 'GET',
        headers: getAuthHeaders() ,
      });
      if (!res.ok) {
        // don't disrupt the page; just skip if not found
        return setScreenConfig(null);
      }
      const data = await res.json();
      const cfg = Array.isArray(data) ? (data[0] || null) : data;
      setScreenConfig(cfg);
    } catch (err) {
      console.error('fetchScreenConfig error:', err);
      setScreenConfig(null);
    }
  };
  // --- /Department screen-config ---
  
  // Effect to fetch vendor data and branches when modal opens
   // Effect to fetch vendor data and branches when modal opens
  useEffect(() => {
    if (open && vendorCode) {
      const fetchVendorData = async () => {
        try {
          // 1. Fetch the complete vendor object using the new API route
          const response = await fetch(`${API_URL}/vendors/${vendorCode}`,{headers: getAuthHeaders() });
          if (!response.ok) {
            throw new Error('Failed to fetch vendor details');
          }
          const data = await response.json();

          // 2. Set the main vendor data
          setVendorData(data);

          // 3. Set addresses and contacts directly from the API response
          // (Based on the backend route provided, these are included in the response)
          setAddresses(data.addresses || []);
          setContacts(data.contacts || []);

          // 4. Fetch supporting data (Branches, Screen Config)
          fetchBranches();
          fetchScreenConfig();
          
          setCurrentStep(0);
        } catch (error) {
          console.error('Error fetching vendor details:', error);
        }
      };

      fetchVendorData();

    } else if (!open) {
      // Reset state when modal is closed
      setVendorData(null);
      setAddresses([]);
      setContacts([]);
      setBranches([]);
      setScreenConfig(null);
      setCurrentStep(0);
      setIsMaximized(false);
    }
  }, [open, vendorCode]);
  
  // Helper function to get branch name from branch code
  const getBranchName = (branchCode) => {
    if (!branchCode) return '-';
    const branch = branches.find(b => b.BranchCode === branchCode);
    return branch ? `${branch.BranchCode} - ${branch.BranchName}` : branchCode;
  };
  
  const handleFileDownload = async (fileType) => {
    try {
      setLoadingFile(fileType);
      const response = await fetch(`${API_URL}/vendors/${vendorData.vendor_code}/files/${fileType}`,{headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${vendorData.vendor_code}_${fileType}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error(`Error downloading ${fileType}:`, error);
      // Replaced alert() with a console log or a custom UI notification
      // alert(`Failed to download ${fileType}: ${error.message}`);
    } finally {
      setLoadingFile(null);
    }
  };
  
  const handleFileView = async (fileType) => {
    try {
      setLoadingFile(fileType);
      const response = await fetch(`${API_URL}/vendors/${vendorData.vendor_code}/files/${fileType}`,{headers: getAuthHeaders() });
      if (!response.ok) {
        throw new Error('Failed to view file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error(`Error viewing ${fileType}:`, error);
      // Replaced alert() with a console log or a custom UI notification
      // alert(`Failed to view ${fileType}: ${error.message}`);
    } finally {
      setLoadingFile(null);
    }
  };
  
  const renderFilePreview = (fileType) => {
    // Add null check for vendorData
    if (!vendorData || !vendorData[fileType]) return '-';
    
    const isLoading = loadingFile === fileType;
    const isImage = fileType === 'logo';
    
    return (
      <div className="flex items-center gap-2">
        {isImage ? (
          <img 
            src={`${API_URL}/vendors/${vendorData.vendor_code}/files/${fileType}`} 
            alt="Company Logo" 
            className="w-10 h-10 object-cover rounded-md"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM2NjY2NjYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIiByeT0iMiI+PC9yZWN0PjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41Ij48L2NpcmNsZT48cG9seWxpbmUgcG9pbnRzPSIyMSAxNSAxNSAxNSA5IDkgMyAxNSI+PC9wb2x5bGluZT48L3N2Zz4=';
            }}
          />
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="text-[#64748b] w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )}
        <button
          onClick={() => handleFileView(fileType)}
          className="p-2 text-[#A0AEC0] hover:text-[#384959] disabled:opacity-50"
          disabled={isLoading}
          title="View"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </button>
        <button
          onClick={() => handleFileDownload(fileType)}
          className="p-2 text-[#A0AEC0] hover:text-[#384959] disabled:opacity-50"
          disabled={isLoading}
          title="Download"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </button>
        {isLoading && <span className="text-sm text-[#64748b]">Loading...</span>}
      </div>
    );
  };
  
  // Create sections only when vendorData is available
   // Create sections only when vendorData is available
  const sections = vendorData ? [
    {
      title: 'Vendor Information',
      fields: [
        // FIX: Added ( ... || '') around values to prevent crash on null
        { label: 'Vendor Code', value: (vendorData.vendor_code || '').toUpperCase() || '-' },
        { label: 'Vendor Name', value: (vendorData.vendor_name || '').toUpperCase() || '-' },
        // { label: 'Vendor Type', value: (vendorData.vendor_type || '').toUpperCase() || '-' },
        // { label: 'Country', value: (vendorData.country || '').toUpperCase() || '-' },
        // { label: 'Email', value: (vendorData.email || '').toUpperCase() || '-' },
        
        // NEW: Showing Phone Number from the first contact
        { 
          label: 'Phone Number', 
          value: (contacts && contacts.length > 0 ? contacts[0].phone_number : '')?.toUpperCase() || '-' 
        },
        
        // NEW: Showing Address Line 1 from the first address
        { 
          label: 'Address Line 1', 
          value: (addresses && addresses.length > 0 ? addresses[0].address_line1 : '')?.toUpperCase() || '-' 
        },

        // { label: 'Status', value: vendorData.status || '-' },
        // { label: 'Branch', value: getBranchName(vendorData.BranchCode) }, 
        
        // Department (single line: Name ID Code) shown when screen-config allows
        // ...(shouldShowDepartmentOnHeader(screenConfig || vendorData?.screenConfig)
        //   ? [{
        //       label: 'Department',
        //       value: `${vendorData?.DepartmentCode || '-'}`,
        //     }]
        //   : []),
      ],
      name: 'Vendor Info'
    }
  ] : [];
  
  const handleNext = () => {
    if (currentStep < sections.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  // Render contacts in a table format
  const renderContacts = () => {
    if (isLoadingContacts) {
      return (
        <div className="text-center py-8 text-[#64748b]">
          <p>Loading contacts...</p>
        </div>
      );
    }
    
    if (contacts.length === 0) {
      return (
        <div className="text-center py-8 text-[#64748b]">
          <p>No contacts available for this vendor.</p>
        </div>
      );
    }
    
    return (
      <div className="contact-table-container">
        <table className="contact-table uppercase">
          <thead>
            <tr>
              <th>Contact Code</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Email</th>
              <th>Phone Number</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((contact, index) => (
              <tr key={contact.contact_id || index}>
                <td className="font-medium">{contact.contact_code || '-'}</td>
                <td className="font-medium">{contact.first_name || '-'}</td>
                <td className="font-medium">{contact.last_name || '-'}</td>
                <td className="max-w-xs break-words">{contact.email || '-'}</td>
                <td>{contact.phone_number || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  // Render addresses in a table format
  const renderAddresses = () => {
    if (isLoadingAddresses) {
      return (
        <div className="text-center py-8 text-[#64748b]">
          <p>Loading addresses...</p>
        </div>
      );
    }
    
    if (addresses.length === 0) {
      return (
        <div className="text-center py-8 text-[#64748b]">
          <p>No addresses available for this vendor.</p>
        </div>
      );
    }
    
    return (
      <div className="address-table-container">
        <table className="address-table uppercase">
          <thead>
            <tr>
              <th>Address Code</th>
              <th>Type</th>
              <th>Address</th>
              <th>City</th>
              <th>State</th>
              <th>Zip Code</th>
              <th>Country</th>
              <th>GST Number</th>
              <th>PAN Number</th>
              <th>Contact Person</th>
              <th>Contact Number</th>
            </tr>
          </thead>
          <tbody>
            {addresses.map((address, index) => (
              <tr key={address.address_id || index}>
                <td className="font-medium">{address.address_code || '-'}</td>
                <td>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium
                    ${address.address_type === 'Registered' ? 'bg-blue-100 text-blue-800' : ''}
                    ${address.address_type === 'Billing' ? 'bg-green-100 text-green-800' : ''}
                    ${address.address_type === 'Shipping' ? 'bg-purple-100 text-purple-800' : ''}
                    ${address.address_type === 'Office' ? 'bg-yellow-100 text-yellow-800' : ''}
                    ${address.address_type === 'Warehouse' ? 'bg-red-100 text-red-800' : ''}
                    ${address.address_type === 'Other' ? 'bg-gray-100 text-gray-800' : ''}
                  `}>
                    {address.address_type || '-'}
                  </span>
                </td>
                <td className="max-w-xs break-words">{address.address_line1 || '-'}</td>
                <td>{address.city || '-'}</td>
                <td>{address.state || '-'}</td>
                <td>{address.zip_code || '-'}</td>
                <td>{address.country || '-'}</td>
                <td>{address.gst_number || '-'}</td>
                <td>{address.pan_number || '-'}</td>
                <td>{address.contact_person_name || '-'}</td>
                <td>{address.contact_person_number || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  // Conditional check to prevent rendering if not open or vendor data is missing
  if (!open || !vendorData) return null;
  
  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ease-in-out
      ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
          .address-table-container, .contact-table-container {
            width: 100%;
            overflow-x: auto;
            padding-bottom: 16px;
            margin-bottom: 16px;
          }
          .address-table, .contact-table {
            width: 100%;
            border-collapse: collapse;
            min-width: 1200px;
          }
          .address-table th, .address-table td, .contact-table th, .contact-table td {
            padding: 12px;
            text-align: left;
            border-bottom: 1px solid #e2e8f0;
            min-width: 120px;
          }
          .address-table th, .contact-table th {
            background-color: #f8fafc;
            font-weight: 600;
            color: #475569;
            font-size: 0.75rem;
            text-transform: uppercase;
            position: sticky;
            top: 0;
          }
          .address-table td, .contact-table td {
            color: #384959;
            font-size: 0.875rem;
          }
          .address-table tr:hover, .contact-table tr:hover {
            background-color: #f8fafc;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div 
        className={`relative bg-white shadow-2xl flex flex-col 
          transition-transform transform-gpu duration-300 ease-in-out
          ${open ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}
          ${isMaximized ? 'w-full h-full max-w-none m-0 rounded-none' : 'w-full max-w-6xl h-full rounded-none'}`}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-[#384959]">Vendor Details</h2>
            <div className="flex gap-2">
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon /> : <MaximizeIcon />}
              </button>
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors" 
                onClick={onClose}
                title="Close"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>
        
        {/* Scrollable Content Area with all sections */}
        <div className="flex-grow overflow-y-auto scrollbar px-8">
          <div className="space-y-6 pb-6">
            {sections.map((section, index) => (
              <div key={index} className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">
                  {section.title}
                </h3>
                
                {/* Special handling for Contact Information section */}
                {section.isContact ? (
                  renderContacts()
                ) : section.isAddress ? (
                  renderAddresses()
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {section.fields?.map((field, fieldIndex) => (
                      <div key={fieldIndex} className="flex flex-col">
                        <span className="text-sm font-medium text-[#64748b]">
                          {field.label}
                        </span>
                        <span className="text-base text-[#384959] mt-1 font-semibold break-words whitespace-normal">
                          {field.label === 'Status' ? (
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold shadow-sm uppercase
                              ${field.value === 'Active' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                              {field.value}
                            </span>
                          ) : (
                            field.value
                          )}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Fixed Footer with Buttons */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
              onClick={onClose}
            >
              Close
            </button>
            {/* <button
              className="px-6 py-3 rounded-lg bg-[#6A89A7] text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 hover:bg-[#88BDF2]"
              onClick={onClose}
            >
              Save Changes
            </button> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorViewModal;